import {
  absoluteToCanonical,
  canonicalToAbsolute,
  decodeAction,
  legalActions,
  type ChessState,
} from "../chess";
import type { GameRenderContext, GameRenderer } from "./types";

// Inline vectors keep the piece design and alignment independent of browser,
// operating-system fonts, and emoji substitution.
const PIECES = [
  "",
  `<svg class="piece-icon" viewBox="0 0 100 100" aria-hidden="true" focusable="false">
    <circle cx="50" cy="23" r="10"/>
    <path d="M39 36h22l3 8H36l3-8Z"/>
    <path d="M40 43h20c0 11 3 19 9 25H31c6-6 9-14 9-25Z"/>
    <path d="M28 66h44l4 8H24l4-8Z"/>
    <path d="M22 73h56c3 0 5 2 5 5v3H17v-3c0-3 2-5 5-5Z"/>
    <path class="piece-detail" d="M22 74h56"/>
  </svg>`,
  `<svg class="piece-icon" viewBox="0 0 100 100" aria-hidden="true" focusable="false">
    <path d="M27 67c2-10 7-19 16-27l-10 2c-5 1-9-1-10-5l19-18 2-10 12 12c12 7 18 19 14 35l-3 12H27Z"/>
    <path class="piece-detail" d="M53 23c10 8 13 20 8 34M43 19l8 5"/>
    <path d="M27 66h43l5 8H23l4-8Z"/>
    <path d="M21 73h58c3 0 5 2 5 5v3H16v-3c0-3 2-5 5-5Z"/>
    <path class="piece-detail" d="M21 74h58"/>
  </svg>`,
  `<svg class="piece-icon" viewBox="0 0 100 100" aria-hidden="true" focusable="false">
    <path d="M50 10c-11 10-17 20-17 30 0 9 5 15 13 19h8c8-4 13-10 13-19 0-10-6-20-17-30Z"/>
    <path class="piece-detail" d="M57 23 43 49"/>
    <path d="M35 56h30l4 11H31l4-11Z"/>
    <path d="M27 66h46l4 8H23l4-8Z"/>
    <path d="M21 73h58c3 0 5 2 5 5v3H16v-3c0-3 2-5 5-5Z"/>
    <path class="piece-detail" d="M21 74h58"/>
  </svg>`,
  `<svg class="piece-icon" viewBox="0 0 100 100" aria-hidden="true" focusable="false">
    <path d="M27 17h12v10h7V17h8v10h7V17h12v25H27V17Z"/>
    <path d="M31 40h38l-4 10H35l-4-10Z"/>
    <path d="M37 48h26l6 20H31l6-20Z"/>
    <path d="M27 66h46l4 8H23l4-8Z"/>
    <path d="M21 73h58c3 0 5 2 5 5v3H16v-3c0-3 2-5 5-5Z"/>
    <path class="piece-detail" d="M31 41h38M21 74h58"/>
  </svg>`,
  `<svg class="piece-icon" viewBox="0 0 100 100" aria-hidden="true" focusable="false">
    <circle cx="22" cy="25" r="5"/><circle cx="36" cy="19" r="5"/>
    <circle cx="50" cy="16" r="5"/><circle cx="64" cy="19" r="5"/>
    <circle cx="78" cy="25" r="5"/>
    <path d="m22 29 10 29h36l10-29-15 15-13-23-13 23-15-15Z"/>
    <path d="M34 55h32l4 12H30l4-12Z"/>
    <path d="M27 66h46l4 8H23l4-8Z"/>
    <path d="M21 73h58c3 0 5 2 5 5v3H16v-3c0-3 2-5 5-5Z"/>
    <path class="piece-detail" d="M31 57h38M21 74h58"/>
  </svg>`,
  `<svg class="piece-icon" viewBox="0 0 100 100" aria-hidden="true" focusable="false">
    <path d="M46 8h8v10h10v8H54v11h-8V26H36v-8h10V8Z"/>
    <path d="M37 43c1-7 6-11 13-11s12 4 13 11l3 12H34l3-12Z"/>
    <path d="M34 53h32l4 14H30l4-14Z"/>
    <path d="M27 66h46l4 8H23l4-8Z"/>
    <path d="M21 73h58c3 0 5 2 5 5v3H16v-3c0-3 2-5 5-5Z"/>
    <path class="piece-detail" d="M33 54h34M21 74h58"/>
  </svg>`,
] as const;

export class ChessRenderer implements GameRenderer<ChessState> {
  #container: HTMLElement | null = null;
  #context: GameRenderContext<ChessState> | null = null;
  #renderedState: ChessState | null = null;
  #selectedSquare = -1;
  #promotionDialog: HTMLDialogElement | null = null;

  mount(container: HTMLElement): void {
    if (this.#container) this.dispose();
    this.#container = container;
    this.#container.classList.add("chess-board");
    this.#container.setAttribute("aria-label", "Chess board");
    this.#container.addEventListener("click", this.#handleClick);
    this.#promotionDialog = document.createElement("dialog");
    this.#promotionDialog.id = "promotion-dialog";
    this.#promotionDialog.innerHTML = `
      <form method="dialog">
        <p>Choose promotion</p>
        <div class="promotion-buttons">
          <button value="q">Queen</button>
          <button value="r">Rook</button>
          <button value="b">Bishop</button>
          <button value="n">Knight</button>
        </div>
      </form>`;
    document.body.append(this.#promotionDialog);
  }

  render(context: GameRenderContext<ChessState>): void {
    const container = this.#container;
    if (!container) throw new Error("Chess renderer is not mounted");
    if (context.state !== this.#renderedState) this.#selectedSquare = -1;
    this.#context = context;
    this.#renderedState = context.state;
    const modelDestination = context.lastModelAction === null
      ? -1
      : canonicalToAbsolute(
        decodeAction(context.lastModelAction.action).to,
        context.lastModelAction.player,
      );

    const destinations = this.#selectedSquare < 0
      ? new Set<number>()
      : new Set(legalActions(context.state).filter((label) => {
        const action = decodeAction(label);
        return canonicalToAbsolute(action.from, context.state.color) ===
          this.#selectedSquare;
      }).map((label) =>
        canonicalToAbsolute(decodeAction(label).to, context.state.color)
      ));
    const squares: string[] = [];
    const firstPlayerAtBottom = context.viewPlayer === 0;
    for (let displayRank = 0; displayRank < 8; displayRank += 1) {
      for (let displayFile = 0; displayFile < 8; displayFile += 1) {
        const file = firstPlayerAtBottom ? displayFile : 7 - displayFile;
        const rank = firstPlayerAtBottom ? 7 - displayRank : displayRank;
        const square = file * 8 + rank;
        const piece = this.#absolutePiece(context.state, square);
        const symbol = PIECES[Math.abs(piece)] ?? "";
        const classes = [
          (file + rank) % 2 ? "dark" : "light",
          this.#selectedSquare === square ? "selected" : "",
          destinations.has(square) ? "legal" : "",
          modelDestination === square ? "ai-move" : "",
        ].filter(Boolean).join(" ");
        const rankCoordinate = displayFile === 0
          ? `<span class="board-coordinate rank-coordinate">${rank + 1}</span>`
          : "";
        const fileCoordinate = displayRank === 7
          ? `<span class="board-coordinate file-coordinate">${String.fromCharCode(97 + file)}</span>`
          : "";
        squares.push(`
          <button class="board-square ${classes}" role="gridcell"
            data-square="${square}" aria-label="${String.fromCharCode(97 + file)}${rank + 1}"
            ${context.interactive ? "" : "disabled"}>
            <span class="piece ${piece < 0 ? "black-piece" : "white-piece"}"
              data-piece="${Math.abs(piece)}">${symbol}</span>
            ${rankCoordinate}
            ${fileCoordinate}
          </button>`);
      }
    }
    container.innerHTML = squares.join("");
  }

  dispose(): void {
    this.#container?.removeEventListener("click", this.#handleClick);
    this.#container?.classList.remove("chess-board");
    this.#container?.removeAttribute("aria-label");
    if (this.#container) this.#container.innerHTML = "";
    this.#promotionDialog?.remove();
    this.#promotionDialog = null;
    this.#container = null;
    this.#context = null;
    this.#renderedState = null;
    this.#selectedSquare = -1;
  }

  #absolutePiece(state: ChessState, square: number): number {
    const canonical = absoluteToCanonical(square, state.color);
    const piece = state.board[canonical] ?? 0;
    return state.color === 0 ? piece : -piece;
  }

  #actionsAt(state: ChessState, from: number, to: number): number[] {
    return legalActions(state).filter((label) => {
      const action = decodeAction(label);
      return canonicalToAbsolute(action.from, state.color) === from &&
        canonicalToAbsolute(action.to, state.color) === to;
    });
  }

  #choosePromotion(actions: number[]): Promise<number> {
    if (actions.length === 1) return Promise.resolve(actions[0]!);
    const dialog = this.#promotionDialog;
    if (!dialog) return Promise.resolve(actions[0]!);
    dialog.showModal();
    return new Promise((resolve) => {
      dialog.addEventListener("close", () => {
        const choice = dialog.returnValue || "q";
        const selected = actions.find((label) => {
          const promotion = decodeAction(label).underpromotion;
          return choice === "q" ? promotion < 0
            : promotion === (
              { r: 0, b: 1, n: 2 } as const
            )[choice as "r" | "b" | "n"];
        });
        resolve(selected ?? actions[0]!);
      }, { once: true });
    });
  }

  #handleClick = (event: Event): void => {
    const context = this.#context;
    if (!context?.interactive) return;
    const button = (event.target as HTMLElement)
      .closest<HTMLButtonElement>("[data-square]");
    if (!button) return;
    const square = Number(button.dataset.square);
    if (this.#selectedSquare >= 0) {
      const actions = this.#actionsAt(
        context.state,
        this.#selectedSquare,
        square,
      );
      if (actions.length) {
        void this.#choosePromotion(actions).then(context.onAction);
        return;
      }
    }
    const piece = this.#absolutePiece(context.state, square);
    const mine = context.state.color === 0 ? piece > 0 : piece < 0;
    this.#selectedSquare = mine ? square : -1;
    this.render(context);
  };
}
